import React, { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';

const formatInactivatedDate = (dateString) => {
  if (!dateString) return null;
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  });
};

function Teachers() {
  const { schoolId } = useParams();
  const apiBase = `/api/admin/${schoolId}`;
  const [teachers, setTeachers] = useState([]);
  const [gradeOptions, setGradeOptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingTeacher, setEditingTeacher] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    grade: '',
    active: true
  });

  const fetchTeachers = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/teachers`);
      setTeachers(response.data);
      setLoading(false);
    } catch (err) {
      setError('Failed to load teachers');
      setLoading(false);
    }
  }, [apiBase]);

  // Grade options come from the same school-scoped settings the public
  // wizard uses (see schools.yaml / GetMenuOptions), so the dropdown here
  // always matches what someone would actually select.
  const fetchGradeOptions = useCallback(async () => {
    try {
      const response = await axios.get(`${apiBase}/options`);
      setGradeOptions(response.data.grade || []);
    } catch (err) {
      setGradeOptions([]);
    }
  }, [apiBase]);

  useEffect(() => {
    fetchTeachers();
    fetchGradeOptions();
  }, [fetchTeachers, fetchGradeOptions]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    try {
      if (editingTeacher) {
        await axios.put(`${apiBase}/teachers/${editingTeacher.id}`, formData);
      } else {
        await axios.post(`${apiBase}/teachers`, formData);
      }

      setFormData({ name: '', grade: '', active: true });
      setShowForm(false);
      setEditingTeacher(null);
      fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save teacher');
    }
  };

  const handleEdit = (teacher) => {
    setEditingTeacher(teacher);
    setFormData({
      name: teacher.name,
      grade: teacher.grade,
      active: teacher.active
    });
    setShowForm(true);
  };

  // Quick one-click toggle from the list, separate from the Edit modal —
  // this is the common case (a teacher left, or a mistakenly-inactivated
  // one is back) and shouldn't need opening the full form just to flip one
  // checkbox. Sends only `active`; the backend sets/clears InactivatedAt
  // itself based on the transition (see handlers.UpdateTeacher).
  const handleToggleActive = async (teacher) => {
    const action = teacher.active ? 'inactivate' : 'reactivate';
    if (!window.confirm(`Are you sure you want to ${action} "${teacher.name}"?`)) {
      return;
    }

    try {
      await axios.put(`${apiBase}/teachers/${teacher.id}`, { active: !teacher.active });
      fetchTeachers();
    } catch (err) {
      setError(err.response?.data?.error || `Failed to ${action} teacher`);
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingTeacher(null);
    setFormData({ name: '', grade: '', active: true });
    setError('');
  };

  if (loading) {
    return <div>Loading teachers...</div>;
  }

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
        <h2>Teacher Management</h2>
        <button
          onClick={() => setShowForm(true)}
          style={{
            padding: '0.75rem 1.5rem',
            backgroundColor: '#007bff',
            color: 'white',
            border: 'none',
            borderRadius: '4px',
            cursor: 'pointer'
          }}
        >
          Add New Teacher
        </button>
      </div>

      {error && (
        <div style={{
          padding: '1rem',
          backgroundColor: '#f8d7da',
          color: '#721c24',
          border: '1px solid #f5c6cb',
          borderRadius: '4px',
          marginBottom: '1rem'
        }}>
          {error}
        </div>
      )}

      {showForm && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            padding: '2rem',
            borderRadius: '8px',
            width: '90%',
            maxWidth: '500px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <h3>{editingTeacher ? 'Edit Teacher' : 'Add New Teacher'}</h3>
            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Teacher Name:
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 'bold' }}>
                  Grade:
                </label>
                <select
                  value={formData.grade}
                  onChange={(e) => setFormData(prev => ({ ...prev, grade: e.target.value }))}
                  required
                  style={{
                    width: '100%',
                    padding: '0.5rem',
                    border: '1px solid #ccc',
                    borderRadius: '4px',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="" disabled>Select a grade</option>
                  {gradeOptions.map(grade => (
                    <option key={grade} value={grade}>{grade}</option>
                  ))}
                </select>
              </div>

              {editingTeacher && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                    <input
                      type="checkbox"
                      checked={formData.active}
                      onChange={(e) => setFormData(prev => ({ ...prev, active: e.target.checked }))}
                    />
                    <span style={{ fontWeight: 'bold' }}>Active</span>
                  </label>
                  <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                    Inactive teachers no longer appear as a choice in the ordering wizard.
                  </p>
                </div>
              )}

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={handleCancel}
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '0.5rem 1rem',
                    backgroundColor: '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer'
                  }}
                >
                  {editingTeacher ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div style={{ display: 'grid', gap: '1rem' }}>
        {teachers.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '2rem', color: '#666' }}>
            No teachers found. Click "Add New Teacher" to get started.
          </div>
        ) : (
          teachers.map(teacher => (
            <div
              key={teacher.id}
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                border: '1px solid #ddd',
                borderRadius: '8px',
                padding: '1rem 1.5rem',
                backgroundColor: teacher.active ? 'white' : '#f8f9fa'
              }}
            >
              <div>
                <strong style={{ color: teacher.active ? '#333' : '#666' }}>
                  {teacher.name}
                </strong>
                <span style={{ color: '#666', marginLeft: '0.75rem' }}>
                  {teacher.grade}
                </span>
                {!teacher.active && (
                  <span style={{ color: '#dc3545', fontSize: '0.8rem', marginLeft: '0.5rem' }}>
                    (Inactive{teacher.inactivated_at ? ` since ${formatInactivatedDate(teacher.inactivated_at)}` : ''})
                  </span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '0.5rem' }}>
                <button
                  onClick={() => handleEdit(teacher)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: '#007bff',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  Edit
                </button>
                <button
                  onClick={() => handleToggleActive(teacher)}
                  style={{
                    padding: '0.25rem 0.5rem',
                    backgroundColor: teacher.active ? '#dc3545' : '#28a745',
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: 'pointer',
                    fontSize: '0.8rem'
                  }}
                >
                  {teacher.active ? 'Inactivate' : 'Reactivate'}
                </button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

export default Teachers;
